import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useBanner } from '@tq/ui-components';

import { BANNER_TIMEOUT } from 'config';
import { consultationIdsToAssignState, selectedConsultationIdsState } from 'store';

type UseAssignModalReturnProps = {
  isConsultationHighlighted: (consultationId: number) => boolean;
  handleClose: (consulteeName?: string) => void;
  handleError: () => void;
  showAssignButton: boolean;
  showAssignModal: boolean;
  handleAssignSelected: () => void;
  handleAssignConsultation: (consultationId: number) => void;
};

export const useAssignModal = (): UseAssignModalReturnProps => {
  const { t } = useTranslation('assign_modal');
  const banner = useBanner();

  const selectedConsultationIds = useRecoilValue(selectedConsultationIdsState);
  const [consultationIdsToAssign, setConsultationIdsToAssign] = useRecoilState(
    consultationIdsToAssignState
  );

  const resetSelectedConsultationIds = useResetRecoilState(selectedConsultationIdsState);
  const resetConsultationIdsToAssign = useResetRecoilState(consultationIdsToAssignState);

  const [consultationIdsToHighlight, setConsultationIdsToHighlight] = useState<number[]>([]);

  const showAssignButton = !!selectedConsultationIds.length;
  const showAssignModal = !!consultationIdsToAssign.length;

  const isConsultationHighlighted = (consultationId: number) =>
    consultationIdsToHighlight.includes(consultationId);

  const handleAssignSelected = () => setConsultationIdsToAssign(selectedConsultationIds);

  const handleAssignConsultation = (consultationId: number) =>
    setConsultationIdsToAssign([consultationId]);

  const handleClose = (consulteeName?: string) => {
    if (consulteeName) {
      setConsultationIdsToHighlight([...consultationIdsToAssign]);
      banner({
        id: 'assign-consultee-banner',
        title: `${
          consultationIdsToAssign.length === 1
            ? t('consultation_assigned', 'Consultation successfully assigned to')
            : t('consultations_assigned', 'Consultations successfully assigned to')
        } ${consulteeName}`,
        duration: BANNER_TIMEOUT,
        onCloseComplete: () => setConsultationIdsToHighlight([]),
      });
    }
    resetSelectedConsultationIds();
    resetConsultationIdsToAssign();
  };

  const handleError = () =>
    banner({
      id: 'assign-consultee-failed-banner',
      title: t('consultation_assign_failed', 'Consultation assignment failed'),
      duration: BANNER_TIMEOUT,
      status: 'error',
    });

  return {
    showAssignModal,
    showAssignButton,
    isConsultationHighlighted,
    handleAssignSelected,
    handleAssignConsultation,
    handleClose,
    handleError,
  };
};
